import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import {
  retrieveContactTagQueryOptions,
  useCreateContactTagConnection,
  useDeleteContactTagConnection,
} from "@/lib/data/react-query";
import { type APIResponse } from "@/lib/types";
import {
  type ContactTag,
  type ContactTagConnection,
} from "@/lib/types/Contact";
import { cn } from "@/lib/utils/cn";
import { getMatterTagChipClassNames } from "@/lib/utils/matters/matter-tag-color";
import { TagIcon } from "@/lib/utils/iconUtils";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { type UseFormReturn } from "react-hook-form";
import type { z } from "zod";
import { ContactSubItemSection } from "../ContactSubItemSection";

export default function ContactTagConnectionsSection({
  tagConnections,
  contactId,
  hasCreatePermission,
  hasDeletePermission,
  parentForm,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  tagConnections?: ContactTagConnection[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
}) {
  const deleteContactTagConnection = useDeleteContactTagConnection();
  const createContactTagConnection = useCreateContactTagConnection();
  const [activeTagConnectionId, setActiveTagConnectionId] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: contactTags, isLoading: isContactTagsLoading } = useQuery<
    APIResponse<ContactTag[]>
  >(retrieveContactTagQueryOptions());
  const parentFormTagConnections = parentForm?.watch("tagConnections") as
    | ContactTagConnection[]
    | undefined;

  const existingTagConnections = useMemo<
    ContactTagConnection[] | undefined
  >(() => {
    if (parentForm) {
      return parentFormTagConnections?.map((connection) => {
        const tag = contactTags?.data.find(
          (contactTag) => contactTag.id === connection.tagId,
        );

        return {
          ...connection,
          color: tag?.color ?? connection.color,
          description: tag?.description ?? connection.description,
          label: tag?.label ?? connection.label,
        };
      });
    }
    return tagConnections;
  }, [parentForm, parentFormTagConnections, contactTags?.data, tagConnections]);

  const activeTagConnection = [
    ...(parentFormTagConnections ?? []),
    ...(tagConnections ?? []),
  ].find((tag) => tag.id === activeTagConnectionId);

  const handleAddTagConnection = async (tagId: string) => {
    if (!tagId) return;
    if (parentForm) {
      const existingTags = parentForm.getValues("tagConnections") ?? [];
      parentForm.setValue(
        "tagConnections",
        [...existingTags, { tagId, id: Math.random().toString() }],
        { shouldDirty: true },
      );
    } else {
      await createContactTagConnection
        .mutateAsync({
          contactId,
          tagId,
          dateModified: undefined,
        })
        .then(() => undefined);
    }
  };

  const handleDeleteTagConnection = (
    tagConnection: Pick<ContactTagConnection, "id" | "contactId" | "tagId">,
  ) => {
    if (parentForm) {
      const existingTags = parentForm.getValues("tagConnections") as
        | ContactTagConnection[]
        | undefined;
      parentForm.setValue(
        "tagConnections",
        existingTags?.filter((tag) => tag.id !== tagConnection.id),
        { shouldDirty: true },
      );
    } else {
      deleteContactTagConnection.mutate({
        id: tagConnection.id,
        contactId: tagConnection.contactId,
      });
    }
    setActiveTagConnectionId(null);
  };

  const availableTags = useMemo(
    () =>
      (contactTags?.data ?? []).filter((tag) =>
        (existingTagConnections ?? []).every(
          (tagConnection) => tag.id !== tagConnection.tagId,
        ),
      ),
    [contactTags?.data, existingTagConnections],
  );
  const isApplyDisabled = isContactTagsLoading || availableTags.length === 0;
  const applyTooltipMessage =
    availableTags.length === 0 ? "All tags already applied." : "";

  return (
    <div>
      <ContactSubItemSection
        icon={<TagIcon className="h-5 w-5 text-gray-400" />}
        label="Tags"
        handleAdd={() => {
          // handled by inline select below
        }}
        className="lg:grid-cols-1"
        hideAddButton
        hasCreatePermission={hasCreatePermission}
      >
        <div className="flex flex-wrap items-baseline gap-2">
          {existingTagConnections && existingTagConnections.length > 0 ? (
            existingTagConnections.map((tag) => (
              <div
                className="group relative"
                key={tag.id}
                data-testid="delete-tag-connection"
              >
                <Badge
                  className={cn(
                    "border px-2.5 py-0.5 text-xs font-semibold",
                    hasDeletePermission ? "pr-7" : "pr-2.5",
                    getMatterTagChipClassNames(tag.color ?? undefined),
                  )}
                >
                  {tag.label}
                </Badge>
                {hasDeletePermission && (
                  <Button
                    type="button"
                    onClick={() => {
                      setActiveTagConnectionId(tag.id);
                      setIsDeleting(true);
                    }}
                    variant="ghost"
                    size="icon"
                    className="bg-muted/30 hover:bg-muted/50 absolute top-1/2 right-1 h-5 w-5 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover:opacity-80"
                    data-testid="delete-tag-connection"
                  >
                    <Trash2 className="text-destructive h-3 w-3" />
                  </Button>
                )}
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">No tags added yet.</p>
          )}
          {hasCreatePermission && (
            <Select
              value=""
              onValueChange={(value) => {
                void handleAddTagConnection(value);
              }}
              disabled={isApplyDisabled}
            >
              <TooltipWrapper message={applyTooltipMessage}>
                <div data-testid="apply-contact-tag">
                  <SelectTrigger
                    className={cn(
                      "h-auto min-h-0 w-fit rounded-full border px-2.5 py-0.5 text-xs font-semibold focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
                      isApplyDisabled && !isContactTagsLoading
                        ? "text-muted-foreground"
                        : null,
                    )}
                  >
                    <SelectValue
                      placeholder={
                        isContactTagsLoading ? "Applying tag..." : "Apply tag"
                      }
                    />
                    {isContactTagsLoading && (
                      <LoadingSpinner className="h-4 w-4" />
                    )}
                  </SelectTrigger>
                </div>
              </TooltipWrapper>
              <SelectContent>
                {isContactTagsLoading && (
                  <SelectItem value="null" disabled>
                    Loading tags...
                  </SelectItem>
                )}
                {!isContactTagsLoading &&
                  availableTags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.label}
                    </SelectItem>
                  ))}
                {!isContactTagsLoading && availableTags.length === 0 && (
                  <SelectItem value="null" disabled>
                    No tags available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        <ActionConfirmationModal
          title={`Are you sure you want to delete this tag?`}
          open={isDeleting && !!activeTagConnection}
          setOpen={setIsDeleting}
          afterActionFn={() => {
            if (activeTagConnection) {
              handleDeleteTagConnection(activeTagConnection);
            }
          }}
        />
      </ContactSubItemSection>
    </div>
  );
}
